<script setup>
import { ref, reactive, computed, onMounted, watch } from "vue";
import { useI18n } from "vue-i18n";

import { FormSheet } from "@/shared/ui/form-sheet";
import { Spinner } from "@/shared/ui/spinner";
import { Empty } from "@/shared/ui/empty";
import Button from "@/shared/ui/button/Button.vue";
import { Input } from "@/shared/ui/input";
import { FormItem, FormLabel, FormControl } from "@/shared/ui/form";
import { Loader2, Check as CheckIcon, Upload } from "lucide-vue-next";
import BaseTabs from "@/shared/ui/custom/tabs/BaseTabs.vue";

import {
  getCabinetAttorneys,
  getTransactionLinks,
  createCabinetTransactionLink,
  removeCabinetTransactionLink,
} from "@/entities/transaction";
import { getStatusLabel } from "@/shared/config/statusDictionary";
import StatusDot from "@/shared/ui/status-dot/StatusDot.vue";

const props = defineProps({
  service: { type: Object, required: true },
  selectedParticipant: { type: Object, default: null },
  item: { type: Object, default: () => ({}) },
  initialTab: { type: String, default: "search" },
});

const emit = defineEmits(["close", "upload-requested"]);

const { t } = useI18n();

const visible = ref(true);
const activeTab = ref(props.initialTab || "search");

const tabs = reactive({
  search: {
    label: t("Поиск доверенностей"),
    icon: "sli-search-drawer",
  },
  links: {
    label: t("Существующие связи"),
    icon: "huge-document-link",
  },
});

// search tab state
const loading = ref(false);
const loadingMore = ref(false);
const hasSearched = ref(false);
const items = ref([]);
const total = ref(0);
const page = ref(1);
const PAGE_SIZE = 20;
const selectedResult = ref(null);
const attaching = ref(false);

const form = reactive({
  number: "",
  identifier: "",
});

// links tab state
const linksLoading = ref(false);
const existingLinks = ref([]);
const selectedLink = ref(null);
const removingLinkId = ref(null);

const hasMore = computed(() => items.value.length < total.value);

const filteredResults = computed(() =>
  items.value.filter((r) => !isAlreadyLinked(r))
);

function isAlreadyLinked(result) {
  if (!result) return false;
  return existingLinks.value.some(
    (link) => link.target_transaction_id === result.id
  );
}

async function fetchPage(targetPage) {
  const params = {
    page: targetPage,
    size: PAGE_SIZE,
  };
  if (form.number) params.number = form.number;
  if (form.identifier) params.identifier = form.identifier;

  const { success, payload } = await getCabinetAttorneys(params);
  return { success, payload };
}

async function load() {
  loading.value = true;
  hasSearched.value = true;
  page.value = 1;
  selectedResult.value = null;
  try {
    const { success, payload } = await fetchPage(1);
    if (success) {
      items.value = payload?.items || [];
      total.value = payload?.total || 0;
    }
  } catch (e) {
    console.error("getCabinetAttorneys error", e);
    items.value = [];
  } finally {
    loading.value = false;
  }
}

async function loadMore() {
  if (loadingMore.value || !hasMore.value) return;
  loadingMore.value = true;
  try {
    const next = page.value + 1;
    const { success, payload } = await fetchPage(next);
    if (success) {
      items.value = [...items.value, ...(payload?.items || [])];
      total.value = payload?.total || total.value;
      page.value = next;
    }
  } catch (e) {
    console.error("getCabinetAttorneys loadMore error", e);
  } finally {
    loadingMore.value = false;
  }
}

async function loadExistingLinks() {
  if (!props.item?.id) return;
  linksLoading.value = true;
  try {
    const data = await getTransactionLinks(props.item.id);
    if (data?.success) {
      const list = data?.payload?.items || data?.payload || [];
      existingLinks.value = Array.isArray(list) ? list : [];
    }
  } catch (e) {
    console.error("getTransactionLinks error", e);
    existingLinks.value = [];
  } finally {
    linksLoading.value = false;
  }
}

function changeTab(tabId) {
  activeTab.value = tabId;
  if (tabId === "links") {
    loadExistingLinks();
  }
}

function selectResult(result) {
  if (isAlreadyLinked(result)) return;
  selectedResult.value = selectedResult.value?.id === result.id ? null : result;
}

function selectLink(link) {
  selectedLink.value = selectedLink.value?.id === link.id ? null : link;
}

async function attachSelected() {
  const result = selectedResult.value;
  if (!result || attaching.value || !props.item?.id) return;

  attaching.value = true;
  try {
    const body = {
      target_transaction_id: result.id,
      note: `Прикреплено из сервиса: ${
        props.service?.title || ""
      }. № действия: ${result.number || ""}`,
    };
    const data = await createCabinetTransactionLink(props.item.id, body);
    if (data?.success || data?.ok) {
      selectedResult.value = null;
      await loadExistingLinks();
    }
  } catch (e) {
    console.error("createCabinetTransactionLink error", e);
  } finally {
    attaching.value = false;
  }
}

async function removeSelected() {
  const link = selectedLink.value;
  if (!link || removingLinkId.value === link.id || !props.item?.id) return;

  removingLinkId.value = link.id;
  try {
    const data = await removeCabinetTransactionLink(props.item.id, link.id);
    if (data?.success || data?.ok) {
      existingLinks.value = existingLinks.value.filter((l) => l.id !== link.id);
      selectedLink.value = null;
    }
  } catch (e) {
    console.error("removeCabinetTransactionLink error", e);
  } finally {
    removingLinkId.value = null;
  }
}

function close() {
  visible.value = false;
  emit("close");
}

function onUploadRequested() {
  emit("upload-requested");
  close();
}

function formatDate(dateString) {
  if (!dateString) return t("Не указано");
  const date = new Date(dateString);
  return (
    date.toLocaleDateString("ru-RU") + " " + date.toLocaleTimeString("ru-RU")
  );
}

function formatNotary(notary) {
  if (!notary) return "";
  return [notary.last_name, notary.first_name, notary.middle_name]
    .filter(Boolean)
    .join(" ");
}

function notaryName(link) {
  return (
    link?.notary_info?.full_name ||
    link?.notary_info?.fio ||
    formatNotary(link?.notary_info) ||
    formatNotary(link?.notary_data)
  );
}

function statusLabel(status) {
  if (!status) return "";
  return getStatusLabel(status) || status;
}

function formatParticipants(participants) {
  if (!Array.isArray(participants) || !participants.length) return "";
  return participants
    .map((p) => {
      if (p?.first_name) {
        return [p.first_name, p.last_name].filter(Boolean).join(" ");
      }
      return p?.params?.company_name || p?.identifier || "";
    })
    .filter(Boolean)
    .join(", ");
}

function formatNumber(number) {
  if (number !== undefined && number !== null) {
    return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  }
  return "";
}

function linkTitle(link) {
  return (
    link?.template_name ||
    link?.template?.code ||
    link?.target_transaction_template_name ||
    t("Доверенность")
  );
}

watch(
  () => activeTab.value,
  (tab) => {
    if (tab === "search") selectedLink.value = null;
    else selectedResult.value = null;
  }
);

onMounted(() => {
  form.identifier = props.selectedParticipant?.identifier || "";
  form.number = props.item?.form || "";
  load();
  loadExistingLinks();
});
</script>

<template>
  <FormSheet
    v-model:open="visible"
    :title="service.title"
    :description="'Прикрепите доверенность'"
    :width="470"
    :close-on-outside="true"
    @close="close"
  >
    <div class="w-full h-full flex flex-col gap-4">
      <BaseTabs
        :tabs="tabs"
        :active-tab="activeTab"
        class="drawer-tabs"
        @change:tab="changeTab"
      />

      <main class="flex flex-col gap-3 flex-1 overflow-y-auto">
        <template v-if="activeTab === 'search'">
          <form
            class="flex flex-col p-4 rounded-md border border-border gap-1"
            @submit.prevent="load"
          >
            <FormItem float>
              <FormLabel>{{ $t("№ нотариального действия") }}</FormLabel>
              <FormControl>
                <Input v-model="form.number" class="h-9 text-sm" />
              </FormControl>
            </FormItem>

            <FormItem float>
              <FormLabel>{{ $t("ИИН") }}</FormLabel>
              <FormControl>
                <Input v-model="form.identifier" class="h-9 text-sm" />
              </FormControl>
            </FormItem>

            <Button
              type="submit"
              variant="outline"
              :disabled="loading"
              class="flex-1 text-sm"
            >
              <Loader2 v-if="loading" class="animate-spin mr-2 w-4 h-4" />
              {{ $t("Поиск") }}
            </Button>
          </form>

          <div v-if="loading" class="flex items-center justify-center py-10">
            <Spinner size="sm" />
          </div>

          <Empty v-else-if="hasSearched && !filteredResults.length">
            {{ $t("Доверенности не найдены или уже прикреплены") }}
            <template #action>
              <Button variant="outline" @click="onUploadRequested">
                <Upload class="w-4 h-4" />
                {{ $t("Загрузить доверенность") }}
              </Button>
            </template>
          </Empty>

          <div
            v-for="link in filteredResults"
            :key="link.id || link.uuid"
            class="link-card"
            :class="{ 'is-selected': selectedResult?.id === link.id }"
            @click="selectResult(link)"
          >
            <div class="link-card__header">
              <div class="link-card__title">{{ linkTitle(link) }}</div>
              <CheckIcon
                v-if="selectedResult?.id === link.id"
                class="link-card__check"
              />
            </div>
            <div class="link-card__rows">
              <div class="link-card__row">
                <div class="link-card__label">
                  {{ $t("№ нотариального действия") }}
                </div>
                <div class="link-card__value">{{ link.number || "—" }}</div>
              </div>
              <div v-if="link.registration_date" class="link-card__row">
                <div class="link-card__label">{{ $t("Дата") }}</div>
                <div class="link-card__value">
                  {{ formatDate(link.registration_date) }}
                </div>
              </div>
              <div v-if="link.template_category_name" class="link-card__row">
                <div class="link-card__label">{{ $t("Категория") }}</div>
                <div class="link-card__value">
                  {{ link.template_category_name }}
                </div>
              </div>
              <div v-if="notaryName(link)" class="link-card__row">
                <div class="link-card__label">{{ $t("Нотариус") }}</div>
                <div class="link-card__value">{{ notaryName(link) }}</div>
              </div>
              <div v-if="link.participants?.length" class="link-card__row">
                <div class="link-card__label">{{ $t("Участники") }}</div>
                <div class="link-card__value">
                  {{ formatParticipants(link.participants) }}
                </div>
              </div>
              <div v-if="link.status" class="link-card__row">
                <div class="link-card__label">{{ $t("Статус") }}</div>
                <div class="link-card__value link-card__status">
                  <StatusDot :code="link.status" />
                  {{ statusLabel(link.status) }}
                </div>
              </div>
            </div>
          </div>

          <Button
            v-if="!loading && hasMore"
            type="button"
            variant="outline"
            :disabled="loadingMore"
            class="self-center text-sm"
            @click="loadMore"
          >
            <Loader2 v-if="loadingMore" class="animate-spin mr-2 w-4 h-4" />
            {{ $t("Загрузить ещё") }}
          </Button>
        </template>

        <template v-else-if="activeTab === 'links'">
          <div
            v-if="linksLoading"
            class="flex items-center justify-center py-10"
          >
            <Spinner size="sm" />
          </div>

          <Empty v-else-if="!existingLinks.length">
            {{ $t("Связи не найдены") }}
          </Empty>

          <div
            v-for="link in existingLinks"
            :key="link.id"
            class="link-card"
            :class="{ 'is-selected': selectedLink?.id === link.id }"
            @click="selectLink(link)"
          >
            <div class="link-card__header">
              <div class="link-card__title">
                {{
                  link?.info?.name ||
                  link?.info?.code ||
                  link?.template_name ||
                  $t("Доверенность")
                }}
              </div>
              <CheckIcon
                v-if="selectedLink?.id === link.id"
                class="link-card__check"
              />
            </div>
            <div class="link-card__rows">
              <div class="link-card__row">
                <div class="link-card__label">
                  {{ $t("№ нотариального действия") }}
                </div>
                <div class="link-card__value">
                  {{ link.number || link.info?.number || "—" }}
                </div>
              </div>
              <div
                v-if="link.info?.registration_date || link.created_at"
                class="link-card__row"
              >
                <div class="link-card__label">{{ $t("Дата") }}</div>
                <div class="link-card__value">
                  {{
                    formatDate(link.info?.registration_date || link.created_at)
                  }}
                </div>
              </div>
              <div
                v-if="
                  link.info?.template_category_name ||
                  link.template_category_name
                "
                class="link-card__row"
              >
                <div class="link-card__label">{{ $t("Категория") }}</div>
                <div class="link-card__value">
                  {{
                    link.info?.template_category_name ||
                    link.template_category_name
                  }}
                </div>
              </div>
              <div v-if="notaryName(link.info || link)" class="link-card__row">
                <div class="link-card__label">{{ $t("Нотариус") }}</div>
                <div class="link-card__value">
                  {{ notaryName(link.info || link) }}
                </div>
              </div>
              <div
                v-if="(link.info?.participants || link.participants)?.length"
                class="link-card__row"
              >
                <div class="link-card__label">{{ $t("Участники") }}</div>
                <div class="link-card__value">
                  {{
                    formatParticipants(
                      link.info?.participants || link.participants
                    )
                  }}
                </div>
              </div>
              <div
                v-if="link.info?.status || link.status"
                class="link-card__row"
              >
                <div class="link-card__label">{{ $t("Статус") }}</div>
                <div class="link-card__value link-card__status">
                  <StatusDot :code="link.info?.status || link.status" />
                  {{ statusLabel(link.info?.status || link.status) }}
                </div>
              </div>
              <div v-if="link.note" class="link-card__row">
                <div class="link-card__label">{{ $t("Примечание") }}</div>
                <div class="link-card__value">{{ link.note }}</div>
              </div>
            </div>
          </div>
        </template>
      </main>
    </div>

    <template #footer>
      <div class="flex-1 text-gray-700 font-bold">
        <template v-if="activeTab === 'search'">
          {{ $t("Всего") }} · {{ formatNumber(total) }}
        </template>
        <template v-else>
          {{ $t("Связей") }} · {{ formatNumber(existingLinks.length) }}
        </template>
      </div>

      <Button variant="outline" type="button" @click="close">
        {{ $t("Закрыть") }}
      </Button>

      <Button
        v-if="activeTab === 'search'"
        type="button"
        :disabled="!selectedResult || attaching"
        @click="attachSelected"
      >
        <Loader2 v-if="attaching" class="animate-spin mr-2 w-4 h-4" />
        {{ $t("Прикрепить") }}
      </Button>

      <Button
        v-else-if="activeTab === 'links'"
        type="button"
        variant="destructive"
        :disabled="!selectedLink || removingLinkId === selectedLink?.id"
        @click="removeSelected"
      >
        <Loader2
          v-if="removingLinkId === selectedLink?.id"
          class="animate-spin mr-2 w-4 h-4"
        />
        {{ $t("Удалить") }}
      </Button>
    </template>
  </FormSheet>
</template>

<style scoped lang="scss">
.drawer-tabs {
  width: 420px !important;
}

.link-card {
  @apply p-4 flex flex-col gap-2 border bg-card transition-colors cursor-pointer;
  border-radius: var(--radius);

  &:hover {
    @apply bg-accent;
  }

  &.is-selected {
    @apply border-primary bg-muted;
  }

  &__header {
    @apply flex items-start justify-between gap-2;
  }

  &__title {
    @apply font-semibold text-foreground text-sm leading-5 mb-1.5;
  }

  &__check {
    @apply text-primary w-4 h-4 shrink-0;
  }

  &__rows {
    @apply flex flex-col gap-2;
  }

  &__row {
    @apply flex flex-col gap-0.5 text-xs;
  }

  &__label {
    @apply text-muted-foreground;
  }

  &__value {
    @apply text-foreground;
    word-break: break-word;
  }

  &__status {
    @apply inline-flex items-center gap-1.5;
  }
}
</style>
